import React, {ReactNode} from "react";
import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import Parameter from "../../parameter/Parameter";
import DialogDefault from "../../parameter/PortalDialogDefault";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import BarcodeSwissQrCodeFormDialogFields from "../../field/barcode/BarcodeSwissQrCodeFormDialogFields";
import {InputText} from "primereact/inputtext";
import {InputNumber} from "primereact/inputnumber";
import {Dropdown} from "primereact/dropdown";
import {CurrencyType, SwissQrCodeForm} from "../../../../shared/models/SwissQrCodeForm";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import {MessageType} from "../../../../shared/log/MessageInterface";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import CodeInput from "../../../../shared/components/CodeInput";
import {
	QrSwissPaymentContent,
	QrSwissPaymentContentFormat
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {PortalBarcode} from "../../../../shared/models/PortalBarcode";
import {PortalAddBarcode, PortalBarcodeFormat} from "../../../../shared/models/PortalAddBarcode";
import {AddressType, SwissQrCodeFormAddress} from "../../../../shared/models/SwissQrCodeFormAddress";

export interface BarcodeSwissQrCodeFormDialogProperties extends PortalDialogProperties<PortalBarcode> {
	format: QrSwissPaymentContentFormat;
	submit: (value: SwissQrCodeForm) => void;
}

export interface BarcodeSwissQrCodeFormDialogState extends PortalDialogState<PortalBarcode> {
	plainView: boolean;
	plainContent: string;
	formValues: SwissQrCodeForm;
}

export class BarcodeSwissQrCodeFormDialogDefinition extends PortalDialog<PortalBarcode,
	BarcodeSwissQrCodeFormDialogProperties, BarcodeSwissQrCodeFormDialogState> {
	/**
	 * Initialize an AbstractDialog component instance
	 */
	constructor(props: Readonly<BarcodeSwissQrCodeFormDialogProperties>) {
		super(props);

		this.state = {
			...this.state,
			plainView: false,
			plainContent: "",
			fieldErrors: new Map<BarcodeSwissQrCodeFormDialogFields, string>(),
			formValues: SwissQrCodeForm.fromJson({})
		};
	}

	public renderContents(): ReactNode {
		let parameter: SwissQrCodeForm | undefined = this.state.formValues;
		let errors: Map<BarcodeSwissQrCodeFormDialogFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		if (this.state.plainView) {
			return (
				<>
					<fieldset className="p-col-12 p-grid">
						<div className="p-col-12 p-grid">
							<label className="p-col-12 label-left" htmlFor="control-swissqrcode-content-textarea">
								<Translate {...this.props} data="barcode.create.content" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-grid">
								<CodeInput inputId="control-swissqrcode-content-textarea" height={"400px"}
										   value={this.state.plainContent}
										   accept={
											   this.props.format === QrSwissPaymentContentFormat.Json ? ".json" : "*.*"
										   }
										   language={
											   this.props.format === QrSwissPaymentContentFormat.Json ? "json" : "text"
										   }
										   className={
											   errors.has(BarcodeSwissQrCodeFormDialogFields.CONTENT) ? "p-invalid" : ""
										   }
										   tooltip={
											   errors.has(BarcodeSwissQrCodeFormDialogFields.CONTENT) ?
												   Translate.value(
													   errors.get(BarcodeSwissQrCodeFormDialogFields.CONTENT)!,
													   {ns: "message"}
												   ) : ""
										   }
										   options={{
											   contextmenu: false,
											   minimap: {
												   enabled: false
											   }
										   }}
										   onChange={(e) => this.onChange(
											   BarcodeSwissQrCodeFormDialogFields.CONTENT, e.value
										   )}/>
								{this.renderFieldError(errors.has(BarcodeSwissQrCodeFormDialogFields.CONTENT) ?
									Translate.value(
										errors.get(BarcodeSwissQrCodeFormDialogFields.CONTENT)!, {ns: "message"}
									) : ""
								)}
							</div>
						</div>
					</fieldset>
				</>
			);
		}

		let creditorAddressType: AddressType = FunctionHelper.applyValue(
			parameter?.creditor?.type, SwissQrCodeFormAddress.getTypeDefault()
		);
		let debtorAddressType: AddressType = FunctionHelper.applyValue(
			parameter?.debtor?.type, SwissQrCodeFormAddress.getTypeDefault()
		);

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(BarcodeSwissQrCodeFormDialogFields.VERSION)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-version">
							<Translate {...this.props} data="barcode.swissqrcode.version" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-version" disabled={true}
									   value={FunctionHelper.applyValue(
										   parameter?.version, SwissQrCodeForm.getVersionDefault()
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(BarcodeSwissQrCodeFormDialogFields.CODING_TYPE)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-coding-type">
							<Translate {...this.props} data="barcode.swissqrcode.coding_type" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-coding-type" disabled={true}
									   value={FunctionHelper.applyValue(
										   parameter?.codingType, SwissQrCodeForm.getVersionDefault()
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(BarcodeSwissQrCodeFormDialogFields.ACCOUNT)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-account">
							<Translate {...this.props} data="barcode.swissqrcode.creditor.account" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-account"
									   className={
										   errors.has(BarcodeSwissQrCodeFormDialogFields.ACCOUNT) ? "p-invalid" : ""
									   }
									   tooltip={errors.has(BarcodeSwissQrCodeFormDialogFields.ACCOUNT) ?
										   Translate.value(
											   errors.get(BarcodeSwissQrCodeFormDialogFields.ACCOUNT)!,
											   {ns: "message"}
										   ) : ""}
									   value={FunctionHelper.applyValue(
										   parameter?.account, SwissQrCodeForm.getAccountDefault()
									   )}
									   onInput={(e) => this.onChange(
										   BarcodeSwissQrCodeFormDialogFields.ACCOUNT, e.currentTarget.value
									   )}/>
							{this.renderFieldError(errors.has(BarcodeSwissQrCodeFormDialogFields.ACCOUNT) ?
								Translate.value(
									errors.get(BarcodeSwissQrCodeFormDialogFields.ACCOUNT)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(BarcodeSwissQrCodeFormDialogFields.CREDITOR_NAME)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-creditor-name">
							<Translate {...this.props} data="barcode.swissqrcode.creditor.name" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-creditor-name"
									   className={
										   errors.has(BarcodeSwissQrCodeFormDialogFields.CREDITOR_NAME) ?
											   "p-invalid" : ""
									   }
									   tooltip={errors.has(BarcodeSwissQrCodeFormDialogFields.CREDITOR_NAME) ?
										   Translate.value(
											   errors.get(BarcodeSwissQrCodeFormDialogFields.CREDITOR_NAME)!,
											   {ns: "message"}
										   ) : ""}
									   value={FunctionHelper.applyValue(
										   parameter?.creditor?.name, SwissQrCodeFormAddress.getNameDefault()
									   )}
									   onInput={(e) => this.onChange(
										   BarcodeSwissQrCodeFormDialogFields.CREDITOR_NAME, e.currentTarget.value
									   )}/>
							{this.renderFieldError(errors.has(BarcodeSwissQrCodeFormDialogFields.CREDITOR_NAME) ?
								Translate.value(
									errors.get(BarcodeSwissQrCodeFormDialogFields.CREDITOR_NAME)!,
									{ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(BarcodeSwissQrCodeFormDialogFields.CREDITOR_TYPE)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-creditor-type">
							<Translate {...this.props} data="barcode.swissqrcode.creditor.type.label"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-creditor-type" value={creditorAddressType}
									  options={[
										  {
											  label: Translate.value(
												  "barcode.swissqrcode.creditor.type.structured",
												  {ns: "dialog"}
											  ),
											  value: AddressType.STRUCTURED
										  },
										  {
											  label: Translate.value(
												  "barcode.swissqrcode.creditor.type.combined",
												  {ns: "dialog"}
											  ),
											  value: AddressType.COMBINED_ELEMENTS
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  BarcodeSwissQrCodeFormDialogFields.CREDITOR_TYPE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(BarcodeSwissQrCodeFormDialogFields.CREDITOR_ADDRESSLINE1) ||
						creditorAddressType === AddressType.STRUCTURED
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-creditor-addressline1">
							<Translate {...this.props} data="barcode.swissqrcode.creditor.addressline1"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-creditor-addressline1"
									   className={
										   errors.has(BarcodeSwissQrCodeFormDialogFields.CREDITOR_ADDRESSLINE1) ?
											   "p-invalid" : ""
									   }
									   tooltip={errors.has(BarcodeSwissQrCodeFormDialogFields.CREDITOR_ADDRESSLINE1) ?
										   Translate.value(
											   errors.get(BarcodeSwissQrCodeFormDialogFields.CREDITOR_ADDRESSLINE1)!,
											   {ns: "message"}
										   ) : ""}
									   value={FunctionHelper.applyValue(
										   parameter?.creditor?.addressLine1,
										   SwissQrCodeFormAddress.getAddressLine1Default()
									   )}
									   onInput={(e) => this.onChange(
										   BarcodeSwissQrCodeFormDialogFields.CREDITOR_ADDRESSLINE1,
										   e.currentTarget.value
									   )}/>
							{this.renderFieldError(
								errors.has(BarcodeSwissQrCodeFormDialogFields.CREDITOR_ADDRESSLINE1) ?
									Translate.value(
										errors.get(BarcodeSwissQrCodeFormDialogFields.CREDITOR_ADDRESSLINE1)!,
										{ns: "message"}
									) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(BarcodeSwissQrCodeFormDialogFields.CREDITOR_ADDRESSLINE2) ||
						creditorAddressType === AddressType.STRUCTURED
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-creditor-addressline2">
							<Translate {...this.props} data="barcode.swissqrcode.creditor.addressline2"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-creditor-addressline2"
									   className={
										   errors.has(BarcodeSwissQrCodeFormDialogFields.CREDITOR_ADDRESSLINE2) ?
											   "p-invalid" : ""
									   }
									   tooltip={errors.has(BarcodeSwissQrCodeFormDialogFields.CREDITOR_ADDRESSLINE2) ?
										   Translate.value(
											   errors.get(BarcodeSwissQrCodeFormDialogFields.CREDITOR_ADDRESSLINE2)!,
											   {ns: "message"}
										   ) : ""}
									   value={FunctionHelper.applyValue(
										   parameter?.creditor?.addressLine2,
										   SwissQrCodeFormAddress.getAddressLine1Default()
									   )}
									   onInput={(e) => this.onChange(
										   BarcodeSwissQrCodeFormDialogFields.CREDITOR_ADDRESSLINE2,
										   e.currentTarget.value
									   )}/>
							{this.renderFieldError(
								errors.has(BarcodeSwissQrCodeFormDialogFields.CREDITOR_ADDRESSLINE2) ?
									Translate.value(
										errors.get(BarcodeSwissQrCodeFormDialogFields.CREDITOR_ADDRESSLINE2)!,
										{ns: "message"}
									) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(BarcodeSwissQrCodeFormDialogFields.CREDITOR_STREET) ||
						creditorAddressType !== AddressType.STRUCTURED
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-creditor-street">
							<Translate {...this.props} data="barcode.swissqrcode.creditor.street" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-creditor-street"
									   className={
										   errors.has(BarcodeSwissQrCodeFormDialogFields.CREDITOR_STREET) ?
											   "p-invalid" : ""
									   }
									   tooltip={errors.has(BarcodeSwissQrCodeFormDialogFields.CREDITOR_STREET) ?
										   Translate.value(
											   errors.get(BarcodeSwissQrCodeFormDialogFields.CREDITOR_STREET)!,
											   {ns: "message"}
										   ) : ""}
									   value={FunctionHelper.applyValue(
										   parameter?.creditor?.street, SwissQrCodeFormAddress.getStreetDefault()
									   )}
									   onInput={(e) => this.onChange(
										   BarcodeSwissQrCodeFormDialogFields.CREDITOR_STREET, e.currentTarget.value
									   )}/>
							{this.renderFieldError(errors.has(BarcodeSwissQrCodeFormDialogFields.CREDITOR_STREET) ?
								Translate.value(
									errors.get(BarcodeSwissQrCodeFormDialogFields.CREDITOR_STREET)!,
									{ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(BarcodeSwissQrCodeFormDialogFields.CREDITOR_HOUSE_NUMBER) ||
						creditorAddressType !== AddressType.STRUCTURED
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-creditor-house-number">
							<Translate {...this.props} data="barcode.swissqrcode.creditor.house_number"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-creditor-house-number"
									   className={
										   errors.has(BarcodeSwissQrCodeFormDialogFields.CREDITOR_HOUSE_NUMBER) ?
											   "p-invalid" : ""
									   }
									   tooltip={errors.has(BarcodeSwissQrCodeFormDialogFields.CREDITOR_HOUSE_NUMBER) ?
										   Translate.value(
											   errors.get(BarcodeSwissQrCodeFormDialogFields.CREDITOR_HOUSE_NUMBER)!,
											   {ns: "message"}
										   ) : ""}
									   value={FunctionHelper.applyValue(
										   parameter?.creditor?.houseNumber,
										   SwissQrCodeFormAddress.getHouseNumberDefault()
									   )}
									   onInput={(e) => this.onChange(
										   BarcodeSwissQrCodeFormDialogFields.CREDITOR_HOUSE_NUMBER,
										   e.currentTarget.value
									   )}/>
							{this.renderFieldError(errors.has(BarcodeSwissQrCodeFormDialogFields.CREDITOR_HOUSE_NUMBER) ?
								Translate.value(
									errors.get(BarcodeSwissQrCodeFormDialogFields.CREDITOR_HOUSE_NUMBER)!,
									{ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(BarcodeSwissQrCodeFormDialogFields.CREDITOR_COUNTRY_CODE)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-creditor-country-code">
							<Translate {...this.props} data="barcode.swissqrcode.creditor.countryCode"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-creditor-country-code"
									   className={
										   errors.has(BarcodeSwissQrCodeFormDialogFields.CREDITOR_COUNTRY_CODE) ?
											   "p-invalid" : ""
									   }
									   tooltip={errors.has(BarcodeSwissQrCodeFormDialogFields.CREDITOR_COUNTRY_CODE) ?
										   Translate.value(
											   errors.get(BarcodeSwissQrCodeFormDialogFields.CREDITOR_COUNTRY_CODE)!,
											   {ns: "message"}
										   ) : ""}
									   value={FunctionHelper.applyValue(
										   parameter?.creditor?.countryCode,
										   SwissQrCodeFormAddress.getCountryCodeDefault()
									   )}
									   onInput={(e) => this.onChange(
										   BarcodeSwissQrCodeFormDialogFields.CREDITOR_COUNTRY_CODE,
										   e.currentTarget.value
									   )}/>
							{this.renderFieldError(errors.has(BarcodeSwissQrCodeFormDialogFields.CREDITOR_COUNTRY_CODE) ?
								Translate.value(
									errors.get(BarcodeSwissQrCodeFormDialogFields.CREDITOR_COUNTRY_CODE)!,
									{ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(BarcodeSwissQrCodeFormDialogFields.CREDITOR_POSTAL_CODE)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-creditor-postal-code">
							<Translate {...this.props} data="barcode.swissqrcode.creditor.postal_code"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-creditor-postal-code"
									   className={
										   errors.has(BarcodeSwissQrCodeFormDialogFields.CREDITOR_POSTAL_CODE) ?
											   "p-invalid" : ""
									   }
									   tooltip={errors.has(BarcodeSwissQrCodeFormDialogFields.CREDITOR_POSTAL_CODE) ?
										   Translate.value(
											   errors.get(BarcodeSwissQrCodeFormDialogFields.CREDITOR_POSTAL_CODE)!,
											   {ns: "message"}
										   ) : ""}
									   value={FunctionHelper.applyValue(
										   parameter?.creditor?.postalCode, SwissQrCodeFormAddress.getPostalCodeDefault()
									   )}
									   onInput={(e) => this.onChange(
										   BarcodeSwissQrCodeFormDialogFields.CREDITOR_POSTAL_CODE,
										   e.currentTarget.value
									   )}/>
							{this.renderFieldError(errors.has(BarcodeSwissQrCodeFormDialogFields.CREDITOR_POSTAL_CODE) ?
								Translate.value(
									errors.get(BarcodeSwissQrCodeFormDialogFields.CREDITOR_POSTAL_CODE)!,
									{ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(BarcodeSwissQrCodeFormDialogFields.CREDITOR_LOCATION)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-creditor-location">
							<Translate {...this.props} data="barcode.swissqrcode.creditor.location" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-creditor-location"
									   className={
										   errors.has(BarcodeSwissQrCodeFormDialogFields.CREDITOR_LOCATION) ?
											   "p-invalid" : ""
									   }
									   tooltip={errors.has(BarcodeSwissQrCodeFormDialogFields.CREDITOR_LOCATION) ?
										   Translate.value(
											   errors.get(BarcodeSwissQrCodeFormDialogFields.CREDITOR_LOCATION)!,
											   {ns: "message"}
										   ) : ""}
									   value={FunctionHelper.applyValue(
										   parameter?.creditor?.location, SwissQrCodeFormAddress.getLocationDefault()
									   )}
									   onInput={(e) => this.onChange(
										   BarcodeSwissQrCodeFormDialogFields.CREDITOR_LOCATION, e.currentTarget.value
									   )}/>
							{this.renderFieldError(errors.has(BarcodeSwissQrCodeFormDialogFields.CREDITOR_LOCATION) ?
								Translate.value(
									errors.get(BarcodeSwissQrCodeFormDialogFields.CREDITOR_LOCATION)!,
									{ns: "message"}
								) : ""
							)}
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(BarcodeSwissQrCodeFormDialogFields.DEBTOR_NAME)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-debtor-name">
							<Translate {...this.props} data="barcode.swissqrcode.debtor.name" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-debtor-name"
									   className={
										   errors.has(BarcodeSwissQrCodeFormDialogFields.DEBTOR_NAME) ? "p-invalid" : ""
									   }
									   tooltip={errors.has(BarcodeSwissQrCodeFormDialogFields.DEBTOR_NAME) ?
										   Translate.value(
											   errors.get(BarcodeSwissQrCodeFormDialogFields.DEBTOR_NAME)!,
											   {ns: "message"}
										   ) : ""}
									   value={FunctionHelper.applyValue(
										   parameter?.debtor?.name, SwissQrCodeFormAddress.getNameDefault()
									   )}
									   onInput={(e) => this.onChange(
										   BarcodeSwissQrCodeFormDialogFields.DEBTOR_NAME, e.currentTarget.value
									   )}/>
							{this.renderFieldError(errors.has(BarcodeSwissQrCodeFormDialogFields.DEBTOR_NAME) ?
								Translate.value(
									errors.get(BarcodeSwissQrCodeFormDialogFields.DEBTOR_NAME)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(BarcodeSwissQrCodeFormDialogFields.DEBTOR_TYPE)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-debtor-type">
							<Translate {...this.props} data="barcode.swissqrcode.debtor.type.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-debtor-type" value={debtorAddressType}
									  options={[
										  {
											  label: Translate.value(
												  "barcode.swissqrcode.debtor.type.structured",
												  {ns: "dialog"}
											  ),
											  value: AddressType.STRUCTURED
										  },
										  {
											  label: Translate.value(
												  "barcode.swissqrcode.debtor.type.combined",
												  {ns: "dialog"}
											  ),
											  value: AddressType.COMBINED_ELEMENTS
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  BarcodeSwissQrCodeFormDialogFields.DEBTOR_TYPE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(BarcodeSwissQrCodeFormDialogFields.DEBTOR_ADDRESSLINE1) ||
						debtorAddressType === AddressType.STRUCTURED
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-debtor-addressline1">
							<Translate {...this.props} data="barcode.swissqrcode.debtor.addressline1"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-debtor-addressline1"
									   className={
										   errors.has(BarcodeSwissQrCodeFormDialogFields.DEBTOR_ADDRESSLINE1) ?
											   "p-invalid" : ""
									   }
									   tooltip={errors.has(BarcodeSwissQrCodeFormDialogFields.DEBTOR_ADDRESSLINE1) ?
										   Translate.value(
											   errors.get(BarcodeSwissQrCodeFormDialogFields.DEBTOR_ADDRESSLINE1)!,
											   {ns: "message"}
										   ) : ""}
									   value={FunctionHelper.applyValue(
										   parameter?.debtor?.addressLine1,
										   SwissQrCodeFormAddress.getAddressLine1Default()
									   )}
									   onInput={(e) => this.onChange(
										   BarcodeSwissQrCodeFormDialogFields.DEBTOR_ADDRESSLINE1,
										   e.currentTarget.value
									   )}/>
							{this.renderFieldError(
								errors.has(BarcodeSwissQrCodeFormDialogFields.DEBTOR_ADDRESSLINE1) ?
									Translate.value(
										errors.get(BarcodeSwissQrCodeFormDialogFields.DEBTOR_ADDRESSLINE1)!,
										{ns: "message"}
									) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(BarcodeSwissQrCodeFormDialogFields.DEBTOR_ADDRESSLINE2) ||
						debtorAddressType === AddressType.STRUCTURED
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-debtor-addressline2">
							<Translate {...this.props} data="barcode.swissqrcode.debtor.addressline2"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-debtor-addressline2"
									   className={
										   errors.has(BarcodeSwissQrCodeFormDialogFields.DEBTOR_ADDRESSLINE2) ?
											   "p-invalid" : ""
									   }
									   tooltip={errors.has(BarcodeSwissQrCodeFormDialogFields.DEBTOR_ADDRESSLINE2) ?
										   Translate.value(
											   errors.get(BarcodeSwissQrCodeFormDialogFields.DEBTOR_ADDRESSLINE2)!,
											   {ns: "message"}
										   ) : ""}
									   value={FunctionHelper.applyValue(
										   parameter?.debtor?.addressLine2,
										   SwissQrCodeFormAddress.getAddressLine1Default()
									   )}
									   onInput={(e) => this.onChange(
										   BarcodeSwissQrCodeFormDialogFields.DEBTOR_ADDRESSLINE2,
										   e.currentTarget.value
									   )}/>
							{this.renderFieldError(
								errors.has(BarcodeSwissQrCodeFormDialogFields.DEBTOR_ADDRESSLINE2) ?
									Translate.value(
										errors.get(BarcodeSwissQrCodeFormDialogFields.DEBTOR_ADDRESSLINE2)!,
										{ns: "message"}
									) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(BarcodeSwissQrCodeFormDialogFields.DEBTOR_STREET) ||
						debtorAddressType !== AddressType.STRUCTURED
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-debtor-street">
							<Translate {...this.props} data="barcode.swissqrcode.debtor.street" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-debtor-street"
									   className={
										   errors.has(BarcodeSwissQrCodeFormDialogFields.DEBTOR_STREET) ?
											   "p-invalid" : ""
									   }
									   tooltip={errors.has(BarcodeSwissQrCodeFormDialogFields.DEBTOR_STREET) ?
										   Translate.value(
											   errors.get(BarcodeSwissQrCodeFormDialogFields.DEBTOR_STREET)!,
											   {ns: "message"}
										   ) : ""}
									   value={FunctionHelper.applyValue(
										   parameter?.debtor?.street, SwissQrCodeFormAddress.getStreetDefault()
									   )}
									   onInput={(e) => this.onChange(
										   BarcodeSwissQrCodeFormDialogFields.DEBTOR_STREET, e.currentTarget.value
									   )}/>
							{this.renderFieldError(errors.has(BarcodeSwissQrCodeFormDialogFields.DEBTOR_STREET) ?
								Translate.value(
									errors.get(BarcodeSwissQrCodeFormDialogFields.DEBTOR_STREET)!,
									{ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(BarcodeSwissQrCodeFormDialogFields.DEBTOR_HOUSE_NUMBER) ||
						debtorAddressType !== AddressType.STRUCTURED
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-debtor-house-number">
							<Translate {...this.props} data="barcode.swissqrcode.debtor.house_number"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-debtor-house-number"
									   className={
										   errors.has(BarcodeSwissQrCodeFormDialogFields.DEBTOR_HOUSE_NUMBER) ?
											   "p-invalid" : ""
									   }
									   tooltip={errors.has(BarcodeSwissQrCodeFormDialogFields.DEBTOR_HOUSE_NUMBER) ?
										   Translate.value(
											   errors.get(BarcodeSwissQrCodeFormDialogFields.DEBTOR_HOUSE_NUMBER)!,
											   {ns: "message"}
										   ) : ""}
									   value={FunctionHelper.applyValue(
										   parameter?.debtor?.houseNumber, SwissQrCodeFormAddress.getHouseNumberDefault()
									   )}
									   onInput={(e) => this.onChange(
										   BarcodeSwissQrCodeFormDialogFields.DEBTOR_HOUSE_NUMBER,
										   e.currentTarget.value
									   )}/>
							{this.renderFieldError(errors.has(BarcodeSwissQrCodeFormDialogFields.DEBTOR_HOUSE_NUMBER) ?
								Translate.value(
									errors.get(BarcodeSwissQrCodeFormDialogFields.DEBTOR_HOUSE_NUMBER)!,
									{ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(BarcodeSwissQrCodeFormDialogFields.DEBTOR_COUNTRY_CODE)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-debtor-country-code">
							<Translate {...this.props} data="barcode.swissqrcode.debtor.countryCode"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-debtor-country-code"
									   className={errors.has(BarcodeSwissQrCodeFormDialogFields.DEBTOR_COUNTRY_CODE) ?
										   "p-invalid" : ""}
									   tooltip={errors.has(BarcodeSwissQrCodeFormDialogFields.DEBTOR_COUNTRY_CODE) ?
										   Translate.value(
											   errors.get(BarcodeSwissQrCodeFormDialogFields.DEBTOR_COUNTRY_CODE)!,
											   {ns: "message"}
										   ) : ""
									   }
									   value={FunctionHelper.applyValue(
										   parameter?.debtor?.countryCode, SwissQrCodeFormAddress.getCountryCodeDefault()
									   )}
									   onInput={(e) => this.onChange(
										   BarcodeSwissQrCodeFormDialogFields.DEBTOR_COUNTRY_CODE,
										   e.currentTarget.value
									   )}/>
							{this.renderFieldError(errors.has(BarcodeSwissQrCodeFormDialogFields.DEBTOR_COUNTRY_CODE) ?
								Translate.value(
									errors.get(BarcodeSwissQrCodeFormDialogFields.DEBTOR_COUNTRY_CODE)!,
									{ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(BarcodeSwissQrCodeFormDialogFields.DEBTOR_POSTAL_CODE)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-debtor-postal-code">
							<Translate {...this.props} data="barcode.swissqrcode.debtor.postal_code"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-debtor-postal-code"
									   className={errors.has(BarcodeSwissQrCodeFormDialogFields.DEBTOR_POSTAL_CODE) ?
										   "p-invalid" : ""}
									   tooltip={errors.has(BarcodeSwissQrCodeFormDialogFields.DEBTOR_POSTAL_CODE) ?
										   Translate.value(
											   errors.get(BarcodeSwissQrCodeFormDialogFields.DEBTOR_POSTAL_CODE)!,
											   {ns: "message"}
										   ) : ""}
									   value={FunctionHelper.applyValue(
										   parameter?.debtor?.postalCode, SwissQrCodeFormAddress.getPostalCodeDefault()
									   )}
									   onInput={(e) => this.onChange(
										   BarcodeSwissQrCodeFormDialogFields.DEBTOR_POSTAL_CODE, e.currentTarget.value
									   )}/>
							{this.renderFieldError(errors.has(BarcodeSwissQrCodeFormDialogFields.DEBTOR_POSTAL_CODE) ?
								Translate.value(
									errors.get(BarcodeSwissQrCodeFormDialogFields.DEBTOR_POSTAL_CODE)!,
									{ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(BarcodeSwissQrCodeFormDialogFields.DEBTOR_LOCATION)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-debtor-location">
							<Translate {...this.props} data="barcode.swissqrcode.debtor.location" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-debtor-location"
									   className={errors.has(BarcodeSwissQrCodeFormDialogFields.DEBTOR_LOCATION) ?
										   "p-invalid" : ""}
									   tooltip={errors.has(BarcodeSwissQrCodeFormDialogFields.DEBTOR_LOCATION) ?
										   Translate.value(
											   errors.get(BarcodeSwissQrCodeFormDialogFields.DEBTOR_LOCATION)!,
											   {ns: "message"}
										   ) : ""}
									   value={FunctionHelper.applyValue(
										   parameter?.debtor?.location, SwissQrCodeFormAddress.getLocationDefault()
									   )}
									   onInput={(e) => this.onChange(
										   BarcodeSwissQrCodeFormDialogFields.DEBTOR_LOCATION, e.currentTarget.value
									   )}/>
							{this.renderFieldError(errors.has(BarcodeSwissQrCodeFormDialogFields.DEBTOR_LOCATION) ?
								Translate.value(
									errors.get(BarcodeSwissQrCodeFormDialogFields.DEBTOR_LOCATION)!,
									{ns: "message"}
								) : ""
							)}
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(BarcodeSwissQrCodeFormDialogFields.AMOUNT)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-amount">
							<Translate {...this.props} data="barcode.swissqrcode.amount" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputNumber id="control-amount" minFractionDigits={2} maxFractionDigits={2}
										 showButtons={true} useGrouping={false}
										 className={errors.has(BarcodeSwissQrCodeFormDialogFields.AMOUNT) ?
											 "p-invalid" : ""}
										 tooltip={errors.has(BarcodeSwissQrCodeFormDialogFields.AMOUNT) ?
											 Translate.value(
												 errors.get(BarcodeSwissQrCodeFormDialogFields.AMOUNT)!,
												 {ns: "message"}
											 ) : ""}
										 value={FunctionHelper.applyValue(
											 parameter?.amount, SwissQrCodeForm.getAmountDefault()
										 )}
										 onChange={(e) => this.onChange(
											 BarcodeSwissQrCodeFormDialogFields.AMOUNT, e.value
										 )}/>
							{this.renderFieldError(errors.has(BarcodeSwissQrCodeFormDialogFields.AMOUNT) ?
								Translate.value(
									errors.get(BarcodeSwissQrCodeFormDialogFields.AMOUNT)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(BarcodeSwissQrCodeFormDialogFields.CURRENCY)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-currency">
							<Translate {...this.props} data="barcode.swissqrcode.currency" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<Dropdown inputId="control-currency"
									  value={FunctionHelper.applyValue(
										  parameter?.currency, SwissQrCodeForm.getCurrencyDefault()
									  )}
									  options={[
										  {label: CurrencyType.CHF, value: CurrencyType.CHF},
										  {label: CurrencyType.EUR, value: CurrencyType.EUR}
									  ]}
									  onChange={(e) => this.onChange(
										  BarcodeSwissQrCodeFormDialogFields.CURRENCY, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(BarcodeSwissQrCodeFormDialogFields.REFERENCE_TYPE)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-reference-type">
							<Translate {...this.props} data="barcode.swissqrcode.reference_type" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-reference-type" disabled={true}
									   value={FunctionHelper.applyValue(
										   parameter?.referenceType, SwissQrCodeForm.getReferenceTypeDefault()
									   )}/>
							{this.renderFieldError(errors.has(BarcodeSwissQrCodeFormDialogFields.REFERENCE_TYPE) ?
								Translate.value(
									errors.get(BarcodeSwissQrCodeFormDialogFields.REFERENCE_TYPE)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(BarcodeSwissQrCodeFormDialogFields.REFERENCE)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-reference">
							<Translate {...this.props} data="barcode.swissqrcode.reference" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-reference"
									   className={
										   errors.has(BarcodeSwissQrCodeFormDialogFields.REFERENCE) ? "p-invalid" : ""
									   }
									   tooltip={errors.has(BarcodeSwissQrCodeFormDialogFields.REFERENCE) ?
										   Translate.value(
											   errors.get(BarcodeSwissQrCodeFormDialogFields.REFERENCE)!,
											   {ns: "message"}
										   ) : ""}
									   value={FunctionHelper.applyValue(
										   parameter?.reference, SwissQrCodeForm.getReferenceDefault()
									   )}
									   onInput={(e) => this.onChange(
										   BarcodeSwissQrCodeFormDialogFields.REFERENCE, e.currentTarget.value
									   )}/>
							{this.renderFieldError(errors.has(BarcodeSwissQrCodeFormDialogFields.REFERENCE) ?
								Translate.value(
									errors.get(BarcodeSwissQrCodeFormDialogFields.REFERENCE)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
					<div className="p-col-12 p-lg-6 p-grid" hidden={
						!this.state.fields?.contains(BarcodeSwissQrCodeFormDialogFields.UNSTRUCTURED_MESSAGE)
					}>
						<label className="p-col-12 p-sm-5" htmlFor="control-unstructured-message">
							<Translate {...this.props} data="barcode.swissqrcode.unstructuredMessage"
									   namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-7 p-grid">
							<InputText id="control-unstructured-message"
									   className={errors.has(BarcodeSwissQrCodeFormDialogFields.UNSTRUCTURED_MESSAGE) ?
										   "p-invalid" : ""}
									   tooltip={errors.has(BarcodeSwissQrCodeFormDialogFields.UNSTRUCTURED_MESSAGE) ?
										   Translate.value(
											   errors.get(BarcodeSwissQrCodeFormDialogFields.UNSTRUCTURED_MESSAGE)!,
											   {ns: "message"}
										   ) : ""}
									   value={FunctionHelper.applyValue(
										   parameter?.unstructuredMessage,
										   SwissQrCodeForm.getUnstructuredMessageDefault()
									   )}
									   onInput={(e) => this.onChange(
										   BarcodeSwissQrCodeFormDialogFields.UNSTRUCTURED_MESSAGE,
										   e.currentTarget.value
									   )}/>
							{this.renderFieldError(errors.has(BarcodeSwissQrCodeFormDialogFields.UNSTRUCTURED_MESSAGE) ?
								Translate.value(
									errors.get(BarcodeSwissQrCodeFormDialogFields.UNSTRUCTURED_MESSAGE)!,
									{ns: "message"}
								) : ""
							)}
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["create-barcode-swissqrcode-dialog", "large"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible}
					onHide={this.hide} appendTo={document.getElementById("root")}
					resizable={false} closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.barcode.swiss.form.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					<Helper {...this.props} type={MessageType.SUCCESS}>
						<Translate {...this.props} data={"dialog.barcode.swiss.form.hint"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected async onChange(field: BarcodeSwissQrCodeFormDialogFields, value: any): Promise<void> {
		let parameter: SwissQrCodeForm = this.state.formValues;
		let errors: Map<BarcodeSwissQrCodeFormDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case BarcodeSwissQrCodeFormDialogFields.ACCOUNT:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.account = value;
				break;
			case BarcodeSwissQrCodeFormDialogFields.CREDITOR_NAME:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.creditor.name = value;
				break;
			case BarcodeSwissQrCodeFormDialogFields.CREDITOR_TYPE:
				parameter.creditor.type = value;
				break;
			case BarcodeSwissQrCodeFormDialogFields.CREDITOR_STREET:
				parameter.creditor.street = value;
				break;
			case BarcodeSwissQrCodeFormDialogFields.CREDITOR_HOUSE_NUMBER:
				parameter.creditor.houseNumber = value;
				break;
			case BarcodeSwissQrCodeFormDialogFields.CREDITOR_POSTAL_CODE:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.creditor.postalCode = value;
				break;
			case BarcodeSwissQrCodeFormDialogFields.CREDITOR_LOCATION:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.creditor.location = value;
				break;
			case BarcodeSwissQrCodeFormDialogFields.CREDITOR_COUNTRY_CODE:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.creditor.countryCode = value;
				break;
			case BarcodeSwissQrCodeFormDialogFields.DEBTOR_NAME:
				parameter.debtor.name = value;
				break;
			case BarcodeSwissQrCodeFormDialogFields.DEBTOR_TYPE:
				parameter.debtor.type = value;
				break;
			case BarcodeSwissQrCodeFormDialogFields.DEBTOR_STREET:
				parameter.debtor.street = value;
				break;
			case BarcodeSwissQrCodeFormDialogFields.DEBTOR_HOUSE_NUMBER:
				parameter.debtor.houseNumber = value;
				break;
			case BarcodeSwissQrCodeFormDialogFields.DEBTOR_POSTAL_CODE:
				parameter.debtor.postalCode = value;
				break;
			case BarcodeSwissQrCodeFormDialogFields.DEBTOR_LOCATION:
				parameter.debtor.location = value;
				break;
			case BarcodeSwissQrCodeFormDialogFields.DEBTOR_COUNTRY_CODE:
				parameter.debtor.countryCode = value;
				break;
			case BarcodeSwissQrCodeFormDialogFields.AMOUNT:
				parameter.amount = value;
				break;
			case BarcodeSwissQrCodeFormDialogFields.CURRENCY:
				parameter.currency = value;
				break;
			case BarcodeSwissQrCodeFormDialogFields.REFERENCE:
				parameter.reference = value;
				parameter.updateReferenceType();
				break;
			case BarcodeSwissQrCodeFormDialogFields.UNSTRUCTURED_MESSAGE:
				parameter.unstructuredMessage = value;
				break;
			case BarcodeSwissQrCodeFormDialogFields.CONTENT:
				this.setState({
					plainContent: value
				});

				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
					break;
				}

				try {
					errors.clear();
					parameter = SwissQrCodeForm.fromSwissQrCode(value, this.props.format);
				} catch (e: any) {
					errors.set(field, "errors.fields.set");
				}
				break;
		}

		this.setState({
			formValues: parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-barcode-create"}/>
					<Translate {...this.props} data="barcode.swissqrcode.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	/**
	 * Render the dialog footer
	 */
	protected renderFooter(): ReactNode {
		let errors: Map<any, string> = this.state.fieldErrors;
		let parameter: SwissQrCodeForm = this.state.formValues;

		return (
			<div className="p-col-12 p-grid">
				<div className="p-col-6 p-grid" style={{
					justifyContent: "flex-start"
				}}>
					<Button className="dialog-button" label={
						this.state.plainView ?
							Translate.value("barcode.swissqrcode.button.form", {ns: "dialog"}) :
							Translate.value("barcode.swissqrcode.button.plain", {ns: "dialog"})
					} onClick={() => {
						errors.delete(BarcodeSwissQrCodeFormDialogFields.CONTENT);

						this.setState({
							plainContent: parameter.toSwissQrCode(this.props.format),
							plainView: !this.state.plainView
						});
					}} style={{
						margin: 0
					}}/>
				</div>
				<div className="p-col-6 p-grid">
					<Button label={Translate.value("buttons.add")} disabled={!this.isValid()}
							icon={"icon" + (this.state.submitted ? " icon-spinner spin" : " icon-check")}
							className="primary-button" onClick={() => this.submit()}/>
					{this.state.submitted ?
						<Button label={Translate.value("buttons.close")} icon="icon icon-close"
								onClick={() => this.hide()} className="dialog-button"/> :
						<Button label={Translate.value("buttons.cancel")} icon="icon icon-close"
								onClick={() => this.hide()} className="dialog-button"/>
					}
				</div>
			</div>
		);
	}

	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: PortalBarcode = this.state.parameter!.data;
		if (!FunctionHelper.exists(parameter)) {
			return;
		}

		let addParameter: PortalAddBarcode = parameter.add as PortalAddBarcode;
		if (addParameter.format !== PortalBarcodeFormat.swissqrcode) {
			return;
		}

		let swissQrContentFormat: QrSwissPaymentContentFormat = FunctionHelper.applyValue(
			addParameter.qrswiss![0]?.content?.format,
			QrSwissPaymentContent.getFormatDefault()
		);

		let swissQrCodeFormType: SwissQrCodeForm = new SwissQrCodeForm({});
		try {
			swissQrCodeFormType = SwissQrCodeForm.fromSwissQrCode(
				addParameter.getFormatContent(), swissQrContentFormat
			);
		} catch (e) {
			// should not occur
		}

		this.setState({
			formValues: swissQrCodeFormType,
			plainContent: swissQrCodeFormType.toSwissQrCode(this.props.format)
		})
	}

	protected isValid(): boolean {
		let parameter: SwissQrCodeForm = this.state.formValues;

		let creditorValid: boolean = false;
		if (parameter?.creditor?.type === AddressType.STRUCTURED) {
			creditorValid =
				!FunctionHelper.isEmpty(parameter?.creditor?.name) &&
				!FunctionHelper.isEmpty(parameter?.creditor?.postalCode) &&
				!FunctionHelper.isEmpty(parameter?.creditor?.location) &&
				!FunctionHelper.isEmpty(parameter?.creditor?.countryCode);
		} else {
			creditorValid =
				!FunctionHelper.isEmpty(parameter?.creditor?.name) &&
				(
					!FunctionHelper.isEmpty(parameter?.creditor?.addressLine1) ||
					!FunctionHelper.isEmpty(parameter?.creditor?.addressLine2)
				) &&
				!FunctionHelper.isEmpty(parameter?.creditor?.countryCode);
		}

		return super.isValid() && parameter.account !== "" && creditorValid;
	}

	protected async handleKeydown(event: KeyboardEvent): Promise<void> {
		if (!this.mounted || !this.props.visible) {
			return;
		}

		let errors: Map<any, string> = this.state.fieldErrors;

		if (event.key.toLowerCase() === "enter" && !this.state.submitted && errors.size === 0) {
			event.stopImmediatePropagation();
		}
	}

	protected async submit(): Promise<void> {
		await super.submit();
		this.props.submit(this.state.formValues);
		this.hide();
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.BARCODE_SWISS_QR_CODE_FORM;
	}

	protected getDefaultParameter(): Parameter<SwissQrCodeForm> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const BarcodeSwissQrCodeFormDialog = React.memo(BarcodeSwissQrCodeFormDialogDefinition,
	(
		prevProps: Readonly<BarcodeSwissQrCodeFormDialogProperties>,
		nextProps: Readonly<BarcodeSwissQrCodeFormDialogProperties>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default BarcodeSwissQrCodeFormDialog;